import { useAuth } from '../contexts/AuthContext'
import {
  isAdminRole,
  canManageTargetUser,
  normalizeRole,
} from '../lib/permissions'

const MODULE_BY_PREFIX: Array<{ prefix: string; module: string }> = [
  { prefix: '/platform', module: 'administration' },
  { prefix: '/leads', module: 'sales' },
  { prefix: '/proposals', module: 'sales' },
  { prefix: '/contracts', module: 'sales' },
  { prefix: '/jobs', module: 'operations' },
  { prefix: '/recurring-jobs', module: 'operations' },
  { prefix: '/dispatch', module: 'operations' },
  { prefix: '/job-calendar', module: 'operations' },
  { prefix: '/time-', module: 'operations' },
  { prefix: '/my-day', module: 'operations' },
  { prefix: '/qc-', module: 'operations' },
  { prefix: '/review', module: 'operations' },
  { prefix: '/end-of-day', module: 'operations' },
  { prefix: '/customers', module: 'customers' },
  // Crew-scoped properties live under operations (assigned jobs), not the admin customers module.
  { prefix: '/my-properties', module: 'operations' },
  { prefix: '/properties', module: 'customers' },
  { prefix: '/profile', module: 'dashboard' },
  { prefix: '/billing', module: 'financial' },
  { prefix: '/invoice', module: 'financial' },
  { prefix: '/payments', module: 'financial' },
  { prefix: '/pnl', module: 'financial' },
  { prefix: '/quickbooks', module: 'financial' },
  { prefix: '/expenses', module: 'financial' },
  { prefix: '/messages', module: 'communication' },
  { prefix: '/inbox', module: 'communication' },
  { prefix: '/team-chat', module: 'communication' },
  { prefix: '/reports', module: 'communication' },
  { prefix: '/settings', module: 'settings' },
  { prefix: '/service-catalog', module: 'settings' },
]

export function moduleForPath(pathname: string): string | null {
  const hit = MODULE_BY_PREFIX.find((r) => pathname === r.prefix || pathname.startsWith(r.prefix + '/') || pathname.startsWith(r.prefix))
  return hit?.module || null
}

export function usePermissions() {
  const { profile, permissions, modules, tenant, isSuperAdmin, permissionLoading } = useAuth()
  const permSet = permissions || new Set<string>()
  const moduleSet = new Set(modules || [])

  const can = (perm: string) => {
    if (!profile) return false
    if (profile.is_active === false) return false
    if (isSuperAdmin) return true
    return permSet.has(perm)
  }

  const canAny = (perms: string[]) => perms.some((p) => can(p))
  const canAll = (perms: string[]) => perms.every((p) => can(p))
  const hasModule = (key: string) => isSuperAdmin || moduleSet.has(key)

  const canManageUser = (targetRole: string) => {
    if (!profile) return false
    if (!can('users.manage')) return false
    return canManageTargetUser(profile.role, normalizeRole(targetRole))
  }

  const canSeePricing = can('financial.pricing_view')

  return {
    profile,
    permissions: permSet,
    modules: moduleSet,
    tenant,
    isSuperAdmin,
    loading: permissionLoading,
    can,
    canAny,
    canAll,
    hasModule,
    canManageUser,
    canSeePricing,
    isAdmin: profile ? isAdminRole(profile.role) || isSuperAdmin : false,
  }
}